import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store';
import { CargoLifecycleProjectionClient } from '../services/cargoLifecycle/CargoLifecycleProjectionClient';
import { isCargoLifecycleProjectionUiEnabled } from '../services/cargoLifecycle/cargoLifecycleProjectionFlags';
import type { CargoLifecycleProjection } from '../services/cargoLifecycle/cargoLifecycleProjectionTypes';

export function useCargoLifecycleProjectionIndex(
  cargoIds: number[],
  options?: { enabled?: boolean; refreshKey?: string }
): {
  index: Map<number, CargoLifecycleProjection>;
  loading: boolean;
  failed: boolean;
} {
  const dispatch = useDispatch<AppDispatch>();
  const projectionUi = isCargoLifecycleProjectionUiEnabled();
  const enabled = (options?.enabled ?? true) && projectionUi;

  const cargoIdsKey = useMemo(
    () =>
      Array.from(new Set(cargoIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)))
        .sort((a, b) => a - b)
        .join(','),
    [cargoIds]
  );

  const [index, setIndex] = useState(() => new Map<number, CargoLifecycleProjection>());
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !cargoIdsKey) {
      setIndex(new Map());
      setLoading(false);
      setFailed(false);
      return;
    }

    const ids = cargoIdsKey.split(',').map(Number);
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    (async () => {
      try {
        const map = await CargoLifecycleProjectionClient.fetchMany(dispatch, ids, {
          forceRefetch: true,
        });
        if (!cancelled) {
          setIndex(map);
          setFailed(map.size === 0 && ids.length > 0);
        }
      } catch {
        if (!cancelled) {
          setIndex(new Map());
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, cargoIdsKey, dispatch, options?.refreshKey]);

  return { index, loading, failed };
}
